/**
 * Fixed setup costs (BR-09, BR-06, BR-07).
 *
 * The shed, the electricity connection, the water pump: the things the farm
 * builds once and uses for years. They are assets under BR-09, not expenses —
 * recording a ৳2,00,000 shed as one month's cost would show a catastrophic
 * loss that month and none of its real cost thereafter.
 *
 * This is a narrower door onto the same asset register the Assets screen
 * shows. It exists because the general form asks a partner to pick a category
 * from a list and know what a salvage value is, while the question actually
 * being asked at this stage of the farm is simpler: what did we build, what
 * did it cost, and which account paid. One register underneath, so
 * depreciation and the asset list keep working on one set of records.
 *
 * Two things happen on save and both matter. The asset goes on the register,
 * and a *pending* `asset_purchase` takes the cost out of the named account
 * (BR-07). The entry stays out of the P&L on purpose: the monthly
 * depreciation is the expense, and counting both would be counting the shed
 * twice.
 */

import { ASSET_CAPITALIZATION_THRESHOLD } from '@agrofarm/shared';
import { useState, type FormEvent } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { todayInDhaka } from '../../lib/date.js';
import { useAuth } from '../auth/AuthContext.js';
import { NewCategoryForm } from './NewCategoryForm.js';
import { useAccounts, useAssetCategories, useCreateAsset, uploadReceipt } from './api.js';

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const DECIMAL_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

/**
 * What this will cost the farm each month once it is depreciating.
 *
 * Shown before saving because it is the number that makes BR-09 make sense to
 * somebody who has just spent two lakh: the money is gone today, but the books
 * will carry it a thousand taka at a time for ten years.
 */
export function monthlyDepreciation(cost: string, usefulLifeYears: number): string | null {
  if (!DECIMAL_DRAFT.test(cost.trim()) || usefulLifeYears <= 0) return null;

  return (Number(cost) / (usefulLifeYears * 12)).toFixed(2);
}

/** BR-09 — at or below the threshold it is an ordinary expense, not an asset. */
export const isCapitalizable = (cost: string): boolean =>
  DECIMAL_DRAFT.test(cost.trim()) && Number(cost) > Number(ASSET_CAPITALIZATION_THRESHOLD);

export function SetupCostsScreen(): JSX.Element {
  const { t } = useTranslation();

  const { data: categories = [] } = useAssetCategories();
  const { data: accounts = [] } = useAccounts();
  const create = useCreateAsset();

  const { user } = useAuth();
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [name, setName] = useState('');
  const [cost, setCost] = useState('');
  const [date, setDate] = useState(todayInDhaka());
  const [accountId, setAccountId] = useState<number | null>(null);
  const [receipt, setReceipt] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [savedName, setSavedName] = useState<string | null>(null);

  const active = accounts.filter((account) => account.isActive);
  const selectedAccount = accountId ?? active[0]?.id ?? null;

  /*
   * The farm's own list, not three names guessed from the seed.
   *
   * These were hardcoded and matched by a regex over the Bangla category
   * names, which broke in both directions: renaming ঘর lost the button, and
   * electricity had no category of its own so it was filed under যন্ত্রপাতি
   * and depreciated over machinery's five years instead of its own life.
   * D-018 made categories admin-managed so the farm decides this.
   */
  const usable = categories.filter((row) => row.isActive !== false);
  const category = usable.find((row) => row.id === categoryId) ?? usable[0];

  // D-018 — the life set on a category decides how every asset under it
  // depreciates, so only an admin may add one. A partner sees the list.
  const canAddCategory = user?.role === 'admin';

  const perMonth = category ? monthlyDepreciation(cost, category.usefulLifeYears) : null;
  const tooSmall = cost.trim() !== '' && DECIMAL_DRAFT.test(cost.trim()) && !isCapitalizable(cost);

  // BR-06 — every asset is above the receipt threshold, so the photo is never
  // optional here. Saying so up front beats a 422 naming a field they cannot see.
  const canSubmit =
    name.trim().length >= 2 &&
    isCapitalizable(cost) &&
    category !== undefined &&
    selectedAccount !== null &&
    receipt !== null &&
    !create.isPending &&
    !isUploading;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrorKey(null);
    setFieldErrors({});
    setSavedName(null);

    try {
      // Uploaded first, so a partner on a weak connection who loses the save
      // has not lost the photo (P1-TR4).
      setIsUploading(true);
      const receiptUrl = await uploadReceipt(receipt!);
      setIsUploading(false);

      await create.mutateAsync({
        name: name.trim(),
        categoryId: category!.id,
        purchaseDate: date,
        cost,
        accountId: selectedAccount!,
        receiptUrl,
      });

      setSavedName(name.trim());
      setName('');
      setCost('');
      setReceipt(null);
    } catch (error) {
      setIsUploading(false);
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('setup.title')}</h1>
      <p className="mb-4 rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-900">{t('setup.explainer')}</p>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <fieldset className="mb-4">
          <legend className="mb-2 text-sm font-medium text-gray-700">{t('setup.kind')}</legend>

          <div className="flex flex-wrap gap-2">
            {usable.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setCategoryId(option.id)}
                aria-pressed={category?.id === option.id}
                className={`min-h-touch rounded-xl px-3 py-3 text-sm font-medium ${
                  category?.id === option.id ? 'bg-green-700 text-white' : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                {option.name}
              </button>
            ))}
          </div>

          {/* Retired from the Assets screen, or a farm that has not seeded. */}
          {usable.length === 0 && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">{t('setup.noCategory')}</p>
          )}

          {canAddCategory &&
            (isAddingCategory ? (
              <NewCategoryForm
                onAdded={(created) => {
                  // Select what was just added: they created it because it is
                  // the one they need.
                  setCategoryId(created.id);
                  setIsAddingCategory(false);
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => setIsAddingCategory(true)}
                className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
              >
                {t('setup.addKind')}
              </button>
            ))}
        </fieldset>

        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="setup-name">
          {t('setup.name')}
        </label>
        <input
          id="setup-name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('setup.namePlaceholder')}
          className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
        />
        {fieldErrors['name'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['name']}</p>}

        <div className="mt-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="setup-cost">
              {t('setup.cost')}
            </label>
            <input
              id="setup-cost"
              inputMode="decimal"
              value={cost}
              onChange={(event) => setCost(event.target.value)}
              placeholder="0.00"
              className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3 text-right tabular-nums"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="setup-date">
              {t('setup.date')}
            </label>
            <input
              id="setup-date"
              type="date"
              value={date}
              max={todayInDhaka()}
              onChange={(event) => setDate(event.target.value)}
              className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-3"
            />
          </div>
        </div>

        {tooSmall && (
          <p className="mt-1 text-sm text-amber-800">
            {t('setup.belowThreshold', { amount: formatMoney(String(ASSET_CAPITALIZATION_THRESHOLD)) })}
          </p>
        )}
        {fieldErrors['cost'] !== undefined && <p className="mt-1 text-sm text-red-700">{fieldErrors['cost']}</p>}

        {/* What BR-09 actually means, in money, before they commit. */}
        {perMonth !== null && category !== undefined && (
          <p aria-live="polite" className="mt-2 rounded-xl bg-white px-4 py-3 text-xs text-gray-600 shadow-sm">
            {t('setup.depreciation', {
              amount: formatMoney(perMonth),
              years: category.usefulLifeYears,
            })}
          </p>
        )}

        <fieldset className="mt-4">
          <legend className="mb-2 text-sm font-medium text-gray-700">{t('setup.paidFrom')}</legend>
          <div className="flex flex-wrap gap-2">
            {active.map((account) => (
              <button
                key={account.id}
                type="button"
                onClick={() => setAccountId(account.id)}
                aria-pressed={selectedAccount === account.id}
                className={`min-h-touch rounded-full px-4 py-2 text-sm ${
                  selectedAccount === account.id
                    ? 'bg-green-700 font-medium text-white'
                    : 'bg-white text-gray-700 shadow-sm'
                }`}
              >
                {account.name}
              </button>
            ))}
          </div>
          {fieldErrors['accountId'] !== undefined && (
            <p className="mt-1 text-sm text-red-700">{fieldErrors['accountId']}</p>
          )}
        </fieldset>

        <label className="mb-1 mt-4 block text-sm font-medium text-gray-700" htmlFor="setup-receipt">
          {t('setup.receipt')}
        </label>
        <input
          id="setup-receipt"
          type="file"
          accept="image/*"
          onChange={(event) => setReceipt(event.target.files?.[0] ?? null)}
          className="min-h-touch w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-gray-500">{t('setup.receiptHint')}</p>
        {fieldErrors['receiptUrl'] !== undefined && (
          <p className="mt-1 text-sm text-red-700">{fieldErrors['receiptUrl']}</p>
        )}

        {/* The money leaves the account, but only once somebody else agrees. */}
        <p className="mt-4 rounded-lg bg-gray-100 px-4 py-3 text-xs text-gray-600">{t('setup.pendingNote')}</p>

        {errorKey !== null && (
          <div role="alert" className="mt-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
            <p>{t(errorKey)}</p>
          </div>
        )}

        {savedName !== null && (
          <div role="status" className="mt-3 rounded-lg bg-green-50 px-4 py-3 text-sm text-green-900">
            <p>{t('setup.saved', { name: savedName })}</p>
          </div>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="mt-4 min-h-[52px] w-full rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
        >
          {t(isUploading || create.isPending ? 'app.saving' : 'setup.submit')}
        </button>
      </form>
    </div>
  );
}
